import { createReducer, on } from '@ngrx/store';
import {
  addExerciseSourceSuccess,
  deleteExerciseSourceSuccess,
  deleteExerciseSuccess,
  readExercise,
  readExerciseFailure,
  readExercises,
  readExercisesFailure,
  readExercisesSuccess,
  readExerciseSuccess,
  restoreExerciseSuccess,
  updateExerciseSourceSuccess,
  updateExerciseSuccess,
} from './exercise.actions';
import { ExerciseDto } from '@shared/dto';
import { PaginatedListQuery } from '@shared/interfaces/paginated-list-query.interface';
import { logout } from '@auth/store/auth.actions';
import { hydrateSuccess } from '@store/storage.actions';
import { State } from '@store/state.interface';

export const EXERCISE_FEATURE_KEY = 'exercise';

export interface ExerciseState extends State<ExerciseDto> {}

export const initialState: ExerciseState = {
  data: null,
  detail: null,
  total: 0,
  query: null,
  loading: false,
  error: null,
};

export const exerciseReducer = createReducer(
  initialState,
  on(readExercises, (state, { query, filters }) => ({
    ...state,
    // Filters live inside the query now, so fold the incoming ones in. Each of
    // query/filters may be omitted on a replay — keep the previous value then.
    query: {
      ...(query ?? state.query),
      filters: filters ?? query?.filters ?? state.query?.filters ?? null,
    },
    loading: true,
    error: null,
  })),
  on(readExercisesSuccess, (state, { data, total }) => ({ ...state, data, total, loading: false })),
  on(readExercisesFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Detail (single entity): the resolver blocks activation and matches by id, so we
  // keep the previous detail during the load (no blank flash on the outgoing page).
  on(readExercise, (state) => ({ ...state, loading: true, error: null })),
  on(readExerciseSuccess, (state, { exercise }) => ({ ...state, detail: exercise, loading: false })),
  on(readExerciseFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Keep the open detail in sync with mutations so the form/restore-banner stay
  // correct without a refetch (the list itself is refetched by reloadAfterMutation$).
  on(updateExerciseSuccess, (state, { exercise }) => ({
    ...state,
    detail: state.detail ? { ...state.detail, ...exercise } : state.detail,
  })),
  on(deleteExerciseSuccess, (state, { exercise }) => ({ ...state, detail: exercise })),
  on(restoreExerciseSuccess, (state, { exercise }) => ({ ...state, detail: exercise })),
  // Asset-manager saves return the whole exercise, but only its sources may be
  // folded in: the user may have unsaved edits to the other fields in the open
  // form, and overwriting the detail wholesale would silently revert them.
  on(addExerciseSourceSuccess, updateExerciseSourceSuccess, deleteExerciseSourceSuccess, (state, { exercise }) => ({
    ...state,
    detail: state.detail ? { ...state.detail, sources: exercise.sources ?? [] } : state.detail,
  })),
  // Create/update/delete/restore change the page contents or totals, so the
  // current page is refetched (see ExerciseEffects.reloadAfterMutation$) rather
  // than mutated in place — which would desync the paginator.
  on(hydrateSuccess, (state, payload) => ({ ...state, ...payload.state.exercise, detail: null })),
  on(logout, () => initialState),
);
